import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Select } from 'antd';
import { withRouter } from 'react-router-dom';
import { parse } from 'qs';
import {
  getCompactMultiSelectMaxTagCount,
  getCompactMultiSelectPlaceholder,
} from '../../../utils/compactMultiSelect';
import { verifyFeatureAccess } from '../../../global/featureFlags';
import { AUTO_UPLOAD_FILTER_KEYS } from '../../chartActions/autoUploadFilterOptions';
import useAutoUploadFilterOptions from '../../chartActions/useAutoUploadFilterOptions';

const AUTO_UPLOAD_KEYS = AUTO_UPLOAD_FILTER_KEYS;

const AutoUploadFilters = ({ updateQuery, location }) => {
  const currentQuery = parse(location.search, { ignoreQueryPrefix: true });

  const selectedValues = useMemo(
    () => AUTO_UPLOAD_KEYS.filter((key) => currentQuery[key] === 'true' || currentQuery[key] === true),
    [currentQuery],
  );

  // The redesigned bars receive these from useReferralFeatureFlags; this legacy
  // sidebar has no flag provider above it, so it resolves them itself.
  const [flags, setFlags] = useState({
    createPatientChart: false,
    taskCreation: false,
    hideTaskButton: false,
    isLoaded: false,
  });

  useEffect(() => {
    let isMounted = true;
    Promise.all([
      verifyFeatureAccess('create_patient_chart'),
      verifyFeatureAccess('task_creation'),
      verifyFeatureAccess('hide_task_creation_button'),
    ]).then(([createPatientChart, taskCreation, hideTaskButton]) => {
      if (isMounted) {
        setFlags({
          createPatientChart: createPatientChart === true,
          taskCreation: taskCreation === true,
          hideTaskButton: hideTaskButton === true,
          isLoaded: true,
        });
      }
    }).catch(() => { /* leave flags off; history still decides */ });

    return () => { isMounted = false; };
  }, []);

  // Availability is only fetched once the flags are known, so an option can never
  // blink out and back in as the two async answers land in either order.
  const options = useAutoUploadFilterOptions({
    ...flags,
    selectedValues,
    enabled: flags.isLoaded,
  });

  const handleChange = (values) => {
    const query = { ...currentQuery };

    // Remove all auto upload keys first
    AUTO_UPLOAD_KEYS.forEach((key) => delete query[key]);

    // Add back only selected ones
    values.forEach((key) => {
      query[key] = true;
    });

    updateQuery(query);
  };

  return (
    <Form.Item
      label="Auto Upload Status"
      style={{ marginTop: 10, display: 'block' }}
    >
      <Select
        className="compact-multi-select"
        mode="multiple"
        allowClear
        placeholder="All statuses"
        options={options}
        onChange={handleChange}
        value={selectedValues}
        style={{ width: '100%' }}
        maxTagCount={getCompactMultiSelectMaxTagCount(selectedValues)}
        maxTagPlaceholder={getCompactMultiSelectPlaceholder('statuses')}
      />
    </Form.Item>
  );
};

AutoUploadFilters.defaultProps = {
  updateQuery: () => {},
  location: {},
};

AutoUploadFilters.propTypes = {
  updateQuery: PropTypes.func,
  location: PropTypes.object,
};

export default withRouter(AutoUploadFilters);
